import { useEffect, useRef } from 'react';
import { isFeatureEnabled } from '../config/featureFlags';

export function useMetronome(
  bpm: number,
  isRunning: boolean,
  beatsPerBar: number,
  noteValue: number,
  subdivision: number,
  onBeat: (beat: number) => void,
  onSubBeat: (subBeat: number) => void,
  disabled: boolean = false
): { reset: () => void } {
  const intervalRef = useRef<number | null>(null);
  const beatCountRef = useRef(0);
  const subBeatCountRef = useRef(0);

  useEffect(() => {
    if (!isRunning || disabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Calculate interval based on note value and subdivision
    // BPM is always in quarter notes per minute
    // We need to convert to the selected note value
    const quarterNoteInterval = (60 / bpm) * 1000;
    
    // Convert to the selected note value
    // If noteValue is 8 (eighth note), we need to double the speed
    // If noteValue is 16 (sixteenth note), we need to quadruple the speed
    const noteValueMultiplier = 4 / noteValue; // 4/4 = 1, 4/8 = 0.5, 4/16 = 0.25
    const baseInterval = quarterNoteInterval * noteValueMultiplier;
    
    // Apply subdivision if it's not 1 (no subdivision) and feature is enabled
    const subdivisionsEnabled = isFeatureEnabled('subdivisions');
    const effectiveSubdivision = subdivisionsEnabled ? subdivision : 1;
    const interval = effectiveSubdivision === 1 ? baseInterval : baseInterval / effectiveSubdivision;

    intervalRef.current = window.setInterval(() => {
      subBeatCountRef.current = (subBeatCountRef.current % effectiveSubdivision) + 1;
      const currentSubBeat = subBeatCountRef.current;

      // Only increment main beat on the first subdivision beat
      if (currentSubBeat === 1) {
        beatCountRef.current = (beatCountRef.current % beatsPerBar) + 1;
        const currentBeat = beatCountRef.current;
        onBeat(currentBeat);

        if (currentBeat === 1) {
          window.dispatchEvent(new CustomEvent("bar"));
        }
      }

      onSubBeat(currentSubBeat);
    }, interval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [bpm, isRunning, beatsPerBar, noteValue, subdivision, onBeat, onSubBeat, disabled]);

  const reset = () => {
    beatCountRef.current = 0;
    subBeatCountRef.current = 0;
  };

  return { reset };
}
